import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Briefcase, Download, RotateCw, Sparkles, AlertTriangle, FileText, CheckCircle2, Calendar } from 'lucide-react';

/**
 * My Briefcase — every leader's one-stop tile (doctrine v1.9).
 * Shows latest Friday report (scoped to caller), latest morning briefing,
 * live anomaly count, and (for CEO only) the latest Monthly Board Pack.
 *
 * Replaces the "hunt through email for your weekly report" problem —
 * everything Mira auto-generates is one click away.
 */
export default function BriefcaseTile({ user }) {
  const isCeo = user?.role === 'ceo';
  const [friday, setFriday] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null);

  const load = async () => {
    setLoading(true);
    const calls = [
      api.get('/mira-friday/latest').catch(() => ({ data: { available: false } })),
      api.get('/mira-intel/briefing').catch(() => ({ data: null })),
      api.get('/mira-intel/anomalies').catch(() => ({ data: { total: 0, top: [] } })),
    ];
    if (isCeo) {
      calls.push(api.get('/mira-board/latest').catch(() => ({ data: { available: false } })));
    }
    const results = await Promise.all(calls);
    setFriday(results[0].data);
    setBriefing(results[1].data);
    setAnomalies(results[2].data);
    if (isCeo) setBoard(results[3].data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user?.email]);

  const regenerateFriday = async () => {
    if (!isCeo) return;
    if (!window.confirm(
      'Regenerate your Friday report now?\n\n' +
      'This pulls fresh data, builds a new Word doc, uploads to Google Drive, ' +
      'and emails you. Takes ~30 seconds.'
    )) return;
    setRefreshing('friday');
    try {
      const scope = isCeo ? 'group' : 'my';
      await api.post('/mira-friday/run-now', { scope });
      await load();
    } catch (err) {
      alert('Regenerate failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setRefreshing(null);
    }
  };

  const regenerateBoard = async () => {
    if (!isCeo) return;
    if (!window.confirm(
      'Regenerate this month\'s Board Pack now?\n\n' +
      'Builds a fresh 12-slide deck from the last 4 Friday reports + live ' +
      'data, uploads to Drive, emails you. Takes ~60 seconds.'
    )) return;
    setRefreshing('board');
    try {
      await api.post('/mira-board/run-now', {});
      await load();
    } catch (err) {
      alert('Regenerate failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setRefreshing(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border bg-white p-4" style={{ borderColor: '#E5E1DC' }} data-testid="briefcase-tile-loading">
        <div className="flex items-center gap-2 text-xs" style={{ color: '#6B7280' }}>
          <div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#2A7B88', borderTopColor: 'transparent' }} />
          Loading your briefcase...
        </div>
      </div>
    );
  }

  const anomalyCount = anomalies?.total || 0;
  const sevColor = anomalyCount === 0 ? '#10B981' :
                   (anomalies?.top || []).some(a => a.severity === 'critical') ? '#DC2626' :
                   (anomalies?.top || []).some(a => a.severity === 'high') ? '#F59E0B' : '#6B7280';

  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="briefcase-tile">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#F3F0EB', background: 'linear-gradient(135deg,#1B2A4A 0%,#2A7B88 100%)' }}>
        <div className="flex items-center gap-2 text-white">
          <Briefcase size={16} />
          <div className="font-semibold text-sm">My Briefcase</div>
          <div className="text-[10px] opacity-70">· everything Mira built for you</div>
        </div>
        <button onClick={load} disabled={!!refreshing}
          className="text-white opacity-80 hover:opacity-100 disabled:opacity-40"
          title="Refresh all"
          data-testid="briefcase-refresh">
          <RotateCw size={14} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* 4-up grid (3-up for non-CEO) */}
      <div className="grid gap-px" style={{ gridTemplateColumns: `repeat(${isCeo ? 4 : 3},minmax(0,1fr))`, backgroundColor: '#F3F0EB' }}>

        {/* ——— Friday Report ——— */}
        <div className="bg-white p-3 flex flex-col gap-2" data-testid="briefcase-friday">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>
            <Calendar size={11} /> Friday Report
          </div>
          {friday?.available ? (
            <>
              <div className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
                {friday.iso_week} · {friday.scope_label || 'Full Group'}
              </div>
              <div className="text-[11px] leading-snug line-clamp-3" style={{ color: '#4B5563' }}>
                {friday.summary_text || '—'}
              </div>
              <div className="flex items-center gap-2 mt-auto pt-2">
                {friday.drive_link && (
                  <a href={friday.drive_link} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 text-[11px] font-medium"
                    style={{ color: '#2A7B88' }}
                    data-testid="briefcase-friday-open">
                    <Download size={11} /> Open deck
                  </a>
                )}
                {isCeo && (
                  <button onClick={regenerateFriday} disabled={refreshing === 'friday'}
                    className="ml-auto text-[10px] underline disabled:opacity-50"
                    style={{ color: '#6B7280' }}
                    data-testid="briefcase-friday-regen">
                    {refreshing === 'friday' ? 'building…' : 'regenerate'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-xs" style={{ color: '#9CA3AF' }}>No report yet.</div>
              <div className="text-[10px]" style={{ color: '#9CA3AF' }}>
                Auto-builds every Friday 16:00 IST.
              </div>
              {isCeo && (
                <button onClick={regenerateFriday} disabled={refreshing === 'friday'}
                  className="text-[11px] font-medium mt-auto pt-2 flex items-center gap-1 disabled:opacity-50"
                  style={{ color: '#2A7B88' }}
                  data-testid="briefcase-friday-build">
                  <Sparkles size={11} /> {refreshing === 'friday' ? 'building…' : 'Build one now'}
                </button>
              )}
            </>
          )}
        </div>

        {/* ——— Morning Briefing ——— */}
        <div className="bg-white p-3 flex flex-col gap-2" data-testid="briefcase-briefing">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>
            <Sparkles size={11} /> Morning Brief
          </div>
          {briefing && (briefing.available !== false) ? (
            <>
              <div className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
                {briefing.date || 'Today'}
              </div>
              <div className="text-[11px] leading-snug line-clamp-4" style={{ color: '#4B5563' }}>
                {(briefing.narrative || briefing.headline || briefing.text || briefing.summary || '').slice(0, 240) || '—'}
              </div>
              <div className="text-[10px] mt-auto pt-2" style={{ color: '#9CA3AF' }}>
                Regenerates daily 07:00 IST
              </div>
            </>
          ) : (
            <div className="text-xs" style={{ color: '#9CA3AF' }}>Arriving at 07:00 IST tomorrow.</div>
          )}
        </div>

        {/* ——— Live Anomalies ——— */}
        <div className="bg-white p-3 flex flex-col gap-2" data-testid="briefcase-anomalies">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>
            <AlertTriangle size={11} /> Live Flags
          </div>
          <div className="text-xl font-bold" style={{ color: sevColor }} data-testid="briefcase-anomaly-count">
            {anomalyCount}
          </div>
          <div className="text-[10px]" style={{ color: '#6B7280' }}>
            {anomalyCount === 0 ? 'All clean.' : 'across 6 detectors'}
          </div>
          {(anomalies?.top || []).slice(0, 2).map((a, i) => (
            <div key={i} className="text-[10px] leading-snug line-clamp-2" style={{ color: '#4B5563' }}>
              • {a.title}
              {a.weather_note && (
                <div className="italic" style={{ color: '#6B7280' }}>↳ {a.weather_note.slice(0, 80)}</div>
              )}
            </div>
          ))}
        </div>

        {/* ——— Board Pack (CEO only) ——— */}
        {isCeo && (
          <div className="bg-white p-3 flex flex-col gap-2" data-testid="briefcase-board">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>
              <FileText size={11} /> Board Pack
            </div>
            {board?.available ? (
              <>
                <div className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
                  {board.month_label}
                </div>
                <div className="text-[11px] leading-snug line-clamp-3" style={{ color: '#4B5563' }}>
                  {(board.headline || '').slice(0, 180) || '—'}
                </div>
                <div className="flex items-center gap-2 mt-auto pt-2">
                  {board.drive_link && (
                    <a href={board.drive_link} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-[11px] font-medium"
                      style={{ color: '#2A7B88' }}
                      data-testid="briefcase-board-open">
                      <Download size={11} /> Open deck
                    </a>
                  )}
                  <button onClick={regenerateBoard} disabled={refreshing === 'board'}
                    className="ml-auto text-[10px] underline disabled:opacity-50"
                    style={{ color: '#6B7280' }}
                    data-testid="briefcase-board-regen">
                    {refreshing === 'board' ? 'building…' : 'regenerate'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-xs" style={{ color: '#9CA3AF' }}>No board pack yet.</div>
                <div className="text-[10px]" style={{ color: '#9CA3AF' }}>
                  Builds monthly on the 25th at 20:00 IST.
                </div>
                <button onClick={regenerateBoard} disabled={refreshing === 'board'}
                  className="text-[11px] font-medium mt-auto pt-2 flex items-center gap-1 disabled:opacity-50"
                  style={{ color: '#2A7B88' }}
                  data-testid="briefcase-board-build">
                  <Sparkles size={11} /> {refreshing === 'board' ? 'building…' : 'Build one now'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
